import Card from '../../../components/card/Card';
import Spinner from '../../../components/Spinner/spinner';
import H from '../../../components/texts/h/H';
import { useClientTheme } from '../../../context/ThemeContext';
import { riskData } from '../../../types/interfaces';
import theme from '../../../styles/theme';
import { RISK_CODES } from '../../../utils/Constants';
import {
  bankClassificationLabel,
  bankDetailLabel,
  bankInstitutionLabel,
  classificationVariant,
  extractClassificationCode,
  formatLidecoAmount,
  formatLidecoLastQuery,
  isTotalConcept,
  listDateLabel,
  listDetailLabel,
  morosoInstitutionLabel,
  negativeSectionItems,
  normalizeBankRatings,
  normalizeNegativeBackground,
  normalizeSummaryRows,
  pickBestNegativeSection,
  sanctionRowLabel,
} from './lidecoSemaforoFormat';

type SanctionItem = {
  sanction?: string;
  sancion?: string;
  from?: string;
  desde?: string;
  to?: string;
  hasta?: string;
  validity?: string;
  vigencia?: string;
  plazo?: string;
  institution?: string;
  institucion?: string;
};

type ListItem = {
  institution?: string;
  institucion?: string;
  description?: string;
  descripcion?: string;
  date?: string;
  fecha?: string;
  detail?: string;
  detalle?: string;
  tipo?: string;
  type?: string;
  tipo_descripcion?: string;
  /** Asuntos concursales (LIDECO / Figma) */
  tipo_asunto?: string;
  tipoAsunto?: string;
  fecha_presentacion?: string;
  fechaPresentacion?: string;
  identificacion_expediente?: string;
  identificacionExpediente?: string;
  identificacion_unica_expediente?: string;
  juzgado?: string;
};

type NegativeSection = {
  recordCount?: number;
  cantidad_de_registros?: number;
  cantidadDeRegistros?: number;
  cantidad?: number;
  cantidad_instituciones?: number;
  items?: unknown[];
  registros?: unknown[];
  instituciones?: unknown[];
  procesos_ejecutivos?: unknown[];
};

type NegativeBackground = {
  cuentas_corrientes_bancarias_sancionadas?: {
    cantidad?: number;
    vigentes?: NegativeSection;
    no_vigentes?: NegativeSection;
  };
  morosos_y_castigados_sistema_financiero?: NegativeSection;
  incumplimientos_empresariales?: NegativeSection;
  procesos_ejecutivos?: NegativeSection;
  asuntos_concursales?: NegativeSection;
  currentSanctions?: NegativeSection;
  vigentes?: NegativeSection;
  expiredSanctions?: NegativeSection;
  no_vigentes?: NegativeSection;
  delinquentAndWrittenOff?: NegativeSection;
  morosos_y_castigados?: NegativeSection;
  current_sanctions?: NegativeSection;
  expired_sanctions?: NegativeSection;
  delinquent_and_written_off?: NegativeSection;
  businessDefaults?: NegativeSection;
  executiveProceedings?: NegativeSection;
  bankruptcyMatters?: NegativeSection;
};

const pickSection = (
  negatives: NegativeBackground | null | undefined,
  ...keys: Array<keyof NegativeBackground | string>
): NegativeSection | undefined => {
  if (!negatives) return undefined;
  for (const key of keys) {
    const block = (negatives as Record<string, NegativeSection | undefined>)[key as string];
    if (block) return block;
  }
  return undefined;
};

const emptyMsg = 'No registra';
const FIGMA_FONT = 'Inter, sans-serif';
const FIGMA_TEXT = theme.colors['secondary-2'].text.regular;
const FIGMA_HAIRLINE = theme.colors.neutral.border.weaker;

const emptyBand: React.CSSProperties = {
  display: 'block',
  width: '100%',
  boxSizing: 'border-box',
  borderTop: `1px solid ${FIGMA_HAIRLINE}`,
  borderBottom: `1px solid ${FIGMA_HAIRLINE}`,
  padding: '12px 0',
  margin: 0,
  textAlign: 'center',
  color: '#5A5E65',
  fontFamily: FIGMA_FONT,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.2,
};

/**
 * Figma Atlantic: same light-grey bar for main section headers only
 * (Central de riesgo, Antecedentes negativos). Subtitles = plain bold text.
 */
const sectionBar: React.CSSProperties = {
  background: theme.colors.neutral.bg.muted,
  color: FIGMA_TEXT,
  padding: '11px 8px',
  fontWeight: 700,
  fontSize: 20,
  marginBottom: 22,
  marginTop: 0,
  borderRadius: 6,
  lineHeight: 1.2,
  fontFamily: FIGMA_FONT,
  letterSpacing: 0.2,
  position: 'relative',
  top: 0.5,
};

/** Figma subtitles (Vigentes, Morosos, Calificación…): bold text, no bar */
const sectionTitle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.2,
  color: FIGMA_TEXT,
  margin: '14px 0 19px',
  fontFamily: FIGMA_FONT,
  letterSpacing: 0.2,
};

const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '8px 0',
  borderBottom: `1px solid ${FIGMA_HAIRLINE}`,
  fontWeight: 700,
  color: '#565A61',
  fontSize: 16,
  lineHeight: 1.25,
  fontFamily: FIGMA_FONT,
};
const thNumStyle: React.CSSProperties = { ...thStyle, textAlign: 'right', paddingRight: 40 };
const thStrongNumStyle: React.CSSProperties = { ...thNumStyle, fontWeight: 700 };
const thCenterStyle: React.CSSProperties = { ...thStyle, textAlign: 'center' };
const tdStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '7.08px 0',
  borderBottom: `1px solid ${FIGMA_HAIRLINE}`,
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.3,
  color: FIGMA_TEXT,
  fontFamily: FIGMA_FONT,
};
const tdNumStyle: React.CSSProperties = {
  ...tdStyle,
  textAlign: 'right',
  paddingRight: 40,
};
const tdCenterStyle: React.CSSProperties = { ...tdStyle, textAlign: 'center' };

/** Shared widths so Fecha columns line up across list-style sections */
const DETAIL_FECHA_COLS = (
  <>
    <col style={{ width: '72%' }} />
    <col style={{ width: '28%' }} />
  </>
);

/** Figma Atlantic: small square risk/class badges (ALTO/5 = terracotta, not DS pill) */
const figmaSquareBadge = (
  _label: string,
  tone: 'success' | 'warning' | 'danger' | 'neutral'
): React.CSSProperties => {
  const bg =
    tone === 'success'
      ? '#2E7D32'
      : tone === 'warning'
        ? '#E65100'
        : tone === 'danger'
          ? '#BA3500' // Figma PNG interior of ALTO / class 5
          : '#6B6B6B';
  return {
    display: 'inline-block',
    background: bg,
    color: theme.colors.neutral.text.inverted,
    padding: '4px 8px',
    borderRadius: 4,
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.15,
    textTransform: 'uppercase' as const,
    letterSpacing: 0.2,
    minWidth: 22,
    minHeight: 22,
    boxSizing: 'border-box',
    textAlign: 'center' as const,
    fontFamily: FIGMA_FONT,
  };
};

/**
 * Figma Atlantic "Realizar consulta": real CSS border (not DS inset shadow).
 * DS Button secondary forces border:none + inset box-shadow — that was the regression.
 */
const figmaConsultaBtn: React.CSSProperties = {
  background: theme.colors.neutral.bg.inverted,
  color: '#E72419',
  border: `1px solid ${theme.colors.danger.border.weak}`,
  borderRadius: 6,
  width: 200,
  padding: '15px 16px',
  fontSize: 16,
  fontWeight: 700,
  cursor: 'pointer',
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
  fontFamily: FIGMA_FONT,
};

interface LidecoSemaforoProps {
  riskData: riskData[] | undefined;
  isLoading: boolean;
  onRefresh: () => void;
  /** Oculta "Realizar consulta" (p. ej. resumen en comité / hideInfo). */
  hideRefresh?: boolean;
}

const itemsOf = <T,>(block?: NegativeSection): T[] =>
  negativeSectionItems(block as Record<string, unknown> | undefined) as T[];

const countOf = (block?: NegativeSection) => {
  const items = negativeSectionItems(block as Record<string, unknown> | undefined);
  const declared =
    block?.recordCount ??
    (block as { record_count?: number } | undefined)?.record_count ??
    block?.cantidad_de_registros ??
    block?.cantidadDeRegistros ??
    block?.cantidad ??
    block?.cantidad_instituciones;
  if (items.some((item) => item != null)) return items.length;
  return declared ?? items.length;
};

const LidecoSemaforo = ({ riskData, isLoading, onRefresh, hideRefresh = false }: LidecoSemaforoProps) => {
  const { clientTheme } = useClientTheme();

  const renderSectionBar = (label: string, extraStyle?: React.CSSProperties) => {
    if (clientTheme) {
      return (
        <Card
          variant='primary'
          modifier='weak'
          spacing='2'
          style={{ marginBottom: 22, ...extraStyle }}
        >
          <H
            type='3'
            variant='neutral-1'
            withMargin={false}
            weight='bold'
            modifier='strong'
          >
            {label}
          </H>
        </Card>
      );
    }

    return <div style={{ ...sectionBar, ...extraStyle }}>{label}</div>;
  };

  const lideco = Array.isArray(riskData)
    ? riskData.find((r) => r.risk_code === RISK_CODES.lideco)
    : undefined;
  const indicators = (lideco as any)?.indicators || [];
  const byCode: Record<string, any> = {};
  indicators.forEach((i: any) => {
    byCode[i.code || i.risk_indicator_code] = i;
  });

  const grade = byCode['LIDECO_001']?.value || '—';
  // last_execution is the time this risk was actually checked. LIDECO_002 is
  // the BCU data cut-off date, which can remain unchanged after a new query.
  const lastQueryRaw =
    lideco?.last_execution || lideco?.execution_date || byCode['LIDECO_002']?.value || '—';
  const banks = normalizeBankRatings(byCode['LIDECO_004']?.value);
  const summary = normalizeSummaryRows(byCode['LIDECO_005']?.value);
  const negativesRaw = normalizeNegativeBackground(byCode['LIDECO_006']?.value);
  const negatives = negativesRaw as NegativeBackground | null;

  const sancionadas = negatives?.cuentas_corrientes_bancarias_sancionadas;
  const vigentes = pickBestNegativeSection(
    pickSection(negatives, 'vigentes', 'currentSanctions', 'current_sanctions'),
    sancionadas?.vigentes as NegativeSection | undefined
  );
  const noVigentes = pickBestNegativeSection(
    pickSection(negatives, 'no_vigentes', 'expiredSanctions', 'expired_sanctions'),
    sancionadas?.no_vigentes as NegativeSection | undefined
  );
  const morosos = pickBestNegativeSection(
    pickSection(
      negatives,
      'morosos_y_castigados_sistema_financiero',
      'morosos_y_castigados',
      'delinquentAndWrittenOff',
      'delinquent_and_written_off'
    )
  );
  const incumplimientos =
    pickSection(negatives, 'incumplimientos_empresariales', 'businessDefaults') ??
    negatives?.incumplimientos_empresariales;
  const ejecutivos =
    pickSection(negatives, 'procesos_ejecutivos', 'executiveProceedings') ??
    negatives?.procesos_ejecutivos;
  const concursales =
    pickSection(negatives, 'asuntos_concursales', 'bankruptcyMatters') ??
    negatives?.asuntos_concursales;

  const gradeVariant =
    String(grade) === '—'
      ? 'neutral'
      : String(grade).toLowerCase() === 'bajo'
      ? 'success'
      : String(grade).toLowerCase() === 'medio'
        ? 'warning'
        : 'danger';

  if (isLoading) {
    return (
      <Card display='flex' justifyContent='center' alignItems='center' height='200px'>
        <Spinner size='lg' variant='primary' />
      </Card>
    );
  }

  return (
    <Card
      style={{ fontFamily: FIGMA_FONT, maxWidth: '100%', minWidth: 0 }}
      margin={{ top: '4' }}
    >
      {renderSectionBar('Central de riesgo')}

      <Card
        display='flex'
        margin={{ bottom: '2' }}
        alignItems='center'
        justifyContent='space-between'
        flexWrap='wrap'
        gap='8px'
      >
        <Card display='flex' alignItems='center' gap='8px' flexWrap='wrap'>
          <span
            style={{
              fontFamily: FIGMA_FONT,
              fontWeight: 700,
              fontSize: 20,
              lineHeight: 1.2,
              color: FIGMA_TEXT,
              letterSpacing: 0.2,
            }}
          >
            Grado de riesgo del BCU
          </span>
          <span style={figmaSquareBadge(String(grade).toUpperCase(), gradeVariant as any)}>
            {String(grade).toUpperCase()}
          </span>
        </Card>
        <Card display='flex' alignItems='center' gap='25px'>
          <span
            style={{
              fontFamily: FIGMA_FONT,
              fontSize: 16,
              lineHeight: 1.2,
              color: FIGMA_TEXT,
            }}
          >
            Última consulta: {formatLidecoLastQuery(lastQueryRaw)}
          </span>
          {!hideRefresh && (
            <button id='lideco-refresh' type='button' onClick={onRefresh} style={figmaConsultaBtn}>
              Realizar consulta
            </button>
          )}
        </Card>
      </Card>

      <div style={{ ...sectionTitle, marginTop: 25 }}>Calificación por banco</div>
      {banks.length === 0 ? (
        <div style={emptyBand}>{emptyMsg}</div>
      ) : (
        <table
          style={{
            width: '100%',
            marginBottom: 24,
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
          }}
        >
          <colgroup>
            <col style={{ width: '33.15%' }} />
            <col style={{ width: '33.7%' }} />
            <col style={{ width: '33.15%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle}>Nombre institución</th>
              <th style={thCenterStyle}>Clasificación</th>
              <th style={thStyle}>Detalle</th>
            </tr>
          </thead>
          <tbody>
            {banks.map((b, idx) => {
              const code = extractClassificationCode(bankClassificationLabel(b));
              // Figma banks: white rows + hairline dividers (no zebra)
              return (
                <tr key={idx}>
                  <td style={tdStyle}>{bankInstitutionLabel(b)}</td>
                  <td style={tdCenterStyle}>
                    <span style={figmaSquareBadge(code, classificationVariant(code))}>{code}</span>
                  </td>
                  <td style={tdStyle}>{bankDetailLabel(b)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      <div style={{ ...sectionTitle, marginBottom: 29, position: 'relative', top: 3 }}>
        Resumen total
      </div>
      {summary.length === 0 ? (
        <div style={emptyBand}>{emptyMsg}</div>
      ) : (
        <table
          style={{
            width: '100%',
            marginBottom: 10,
            borderCollapse: 'collapse',
            tableLayout: 'fixed',
            position: 'relative',
            top: 0.5,
          }}
        >
          <colgroup>
            <col style={{ width: '26%' }} />
            <col style={{ width: '25.86%' }} />
            <col style={{ width: '25.12%' }} />
            <col style={{ width: '23.02%' }} />
          </colgroup>
          <thead>
            <tr>
              <th style={thStyle} />
              <th style={thNumStyle}>Moneda Nacional en $</th>
              <th style={thNumStyle}>Moneda Extranjera en U$S</th>
              <th style={{ ...thStrongNumStyle, transform: 'translateX(40px)' }}>
                Total Equivalente en U$S
              </th>
            </tr>
          </thead>
          <tbody>
            {summary.map((s, idx) => {
              const label = String(s.concept ?? s.concepto ?? '—');
              const bold = isTotalConcept(label);
              const startsRiskPotentialBlock = label.trim().toLowerCase() === 'contingencias';
              const startsWrittenOffBlock = label.startsWith('Créditos Castigados');
              const startsNonComputableBlock = label === 'Garantías No Computables';
              const endsVisualGroup =
                label === 'Total Créditos' ||
                label === 'Total Riesgos Potenciales' ||
                label === 'Garantías Computables' ||
                label === 'Previsiones';
              const groupedPaddingTop = startsWrittenOffBlock
                ? 24.5
                : startsRiskPotentialBlock || startsNonComputableBlock
                  ? 24
                  : undefined;
              // Figma resumen: white rows + hairlines (no zebra)
              const cell: React.CSSProperties = {
                ...tdStyle,
                fontWeight: bold ? 700 : 400,
                ...(endsVisualGroup ? { borderBottom: 'none' } : {}),
                ...(groupedPaddingTop ? { paddingTop: groupedPaddingTop } : {}),
              };
              const cellNum: React.CSSProperties = {
                ...tdNumStyle,
                fontWeight: bold ? 700 : 400,
                ...(endsVisualGroup ? { borderBottom: 'none' } : {}),
                ...(groupedPaddingTop ? { paddingTop: groupedPaddingTop } : {}),
              };
              return (
                <tr key={idx}>
                  <td style={cell}>{label}</td>
                  <td style={cellNum}>{formatLidecoAmount(String(s.nationalCurrency ?? s.moneda_nacional ?? ''))}</td>
                  <td style={cellNum}>{formatLidecoAmount(String(s.foreignCurrency ?? s.moneda_extranjera ?? ''))}</td>
                  <td style={{ ...cellNum, transform: 'translateX(40px)' }}>
                    {formatLidecoAmount(String(s.totalUsdEquivalent ?? s.total_equivalente_usd ?? ''))}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {renderSectionBar('Antecedentes negativos', { marginTop: 24, top: 1.5 })}

      <SanctionBlock title='Vigentes' items={itemsOf<SanctionItem>(vigentes)} />
      <SanctionBlock
        title={`No vigentes (${countOf(noVigentes)})`}
        items={itemsOf<SanctionItem>(noVigentes)}
      />
      <MorososBlock
        title={`Morosos y Castigados por el Sistema Financiero (${countOf(morosos)})`}
        items={itemsOf<ListItem | string>(morosos)}
      />
      <DetailFechaBlock
        title={
          countOf(incumplimientos)
            ? `Registro de incumplimientos empresariales (${countOf(incumplimientos)})`
            : 'Registro de incumplimientos empresariales'
        }
        items={itemsOf<ListItem>(incumplimientos)}
      />
      <DetailFechaBlock
        title={
          countOf(ejecutivos)
            ? `Procesos ejecutivos (${countOf(ejecutivos)})`
            : 'Procesos ejecutivos'
        }
        items={itemsOf<ListItem>(ejecutivos)}
      />
      <ConcursalesBlock
        title={`Asuntos Concursales (${countOf(concursales)})`}
        items={itemsOf<ListItem>(concursales)}
      />
      {/* padding (not margin) so it does not collapse with footer margin-top */}
      <div aria-hidden style={{ height: 8 }} />
    </Card>
  );
};

const SanctionBlock = ({ title, items }: { title?: string; items: SanctionItem[] }) => (
  <Card
    margin={{ bottom: '4' }}
    style={title?.startsWith('No vigentes') ? { marginTop: 22.5 } : undefined}
  >
    {title ? <div style={sectionTitle}>{title}</div> : null}
    {items.length === 0 ? (
      <div style={emptyBand}>{emptyMsg}</div>
    ) : (
      <table
        style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed',
          position: 'relative',
          top: -3,
        }}
      >
        <colgroup>
          <col style={{ width: '19.43%' }} />
          <col style={{ width: '13.17%' }} />
          <col style={{ width: '16.43%' }} />
          <col style={{ width: '13%' }} />
          <col style={{ width: '37.97%' }} />
        </colgroup>
        <thead>
          <tr>
            <th style={thStyle}>Sanción</th>
            <th style={{ ...thCenterStyle, transform: 'translateX(19px)' }}>Desde</th>
            <th style={{ ...thCenterStyle, transform: 'translateX(21px)' }}>Hasta</th>
            <th style={{ ...thCenterStyle, transform: 'translateX(11px)' }}>Plazo</th>
            <th style={{ ...thNumStyle, paddingRight: 46 }}>Institución</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i, idx) => (
            <tr key={idx}>
              <td style={tdStyle}>{sanctionRowLabel(i, 'sanction')}</td>
              <td style={tdCenterStyle}>{sanctionRowLabel(i, 'from')}</td>
              <td style={tdCenterStyle}>{sanctionRowLabel(i, 'to')}</td>
              <td style={{ ...tdCenterStyle, transform: 'translateX(12px)' }}>
                {sanctionRowLabel(i, 'validity')}
              </td>
              <td style={{ ...tdNumStyle, paddingRight: 40 }}>
                {sanctionRowLabel(i, 'institution')}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);

/** Figma/LIDECO: institution list only — no Fecha column; no "Actualización" meta (not in Figma). */
const MorososBlock = ({ title, items }: { title: string; items: (ListItem | string)[] }) => (
  <Card margin={{ bottom: '4' }} style={{ marginTop: 5.5, marginBottom: 25.5 }}>
    <div style={sectionTitle}>{title}</div>
    {items.length === 0 ? (
      <div style={emptyBand}>{emptyMsg}</div>
    ) : (
      <>
        <div style={{ width: '49.25%' }}>
          <div
            style={{
              padding: '10px 0 8px',
              color: FIGMA_TEXT,
              fontFamily: FIGMA_FONT,
              fontSize: 16,
              fontWeight: 700,
              lineHeight: 1.25,
              letterSpacing: 0.3,
            }}
          >
            Instituciones en las que presenta Créditos Morosos o Castigados
          </div>
          {items.map((i, idx) => (
            <div
              key={idx}
              style={{
                ...tdStyle,
                borderBottom: `1px solid ${FIGMA_HAIRLINE}`,
                padding: '7px 0',
                listStyle: 'none',
              }}
            >
              {typeof i === 'string'
                ? i
                : morosoInstitutionLabel(i)}
            </div>
          ))}
        </div>
      </>
    )}
  </Card>
);

/** Shared Detalle + Fecha table — fixed columns so Fecha aligns across sections. */
const DetailFechaBlock = ({ title, items }: { title: string; items: ListItem[] }) => (
  <Card margin={{ bottom: '4' }} style={{ marginBottom: 21 }}>
    <div style={sectionTitle}>{title}</div>
    {items.length === 0 ? (
      <div style={{ ...emptyBand, padding: '10.5px 0' }}>{emptyMsg}</div>
    ) : (
      <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
        <colgroup>{DETAIL_FECHA_COLS}</colgroup>
        <thead>
          <tr>
            <th style={thStyle}>Detalle</th>
            <th style={thNumStyle}>Fecha</th>
          </tr>
        </thead>
        <tbody>
          {items.map((i, idx) => (
            <tr key={idx}>
              <td style={tdStyle}>{listDetailLabel(i)}</td>
              <td style={tdNumStyle}>{listDateLabel(i)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    )}
  </Card>
);

/**
 * Figma Atlantic truth (user crop): title without grey bar; 4 fields as
 * label-above-value columns; no Fuente/Referencias; no table chrome.
 */
const concursalesTitleStyle: React.CSSProperties = {
  fontSize: 20,
  fontWeight: 700,
  lineHeight: 1.2,
  color: FIGMA_TEXT,
  margin: '21.5px 0 18px',
  fontFamily: 'Inter, sans-serif',
};
const concursalesLabelStyle: React.CSSProperties = {
  fontSize: 16,
  fontWeight: 400,
  lineHeight: 1.25,
  color: FIGMA_TEXT,
  marginBottom: 8,
  fontFamily: 'Inter, sans-serif',
  letterSpacing: 0.2,
};
const concursalesValueStyle: React.CSSProperties = {
  fontSize: 24,
  fontWeight: 700,
  lineHeight: 1.3,
  color: FIGMA_TEXT,
  fontFamily: 'Inter, sans-serif',
};
const ConcursalesField = ({ label, value, flex }: { label: string; value: string; flex: number }) => (
  <div
    style={{
      flex,
      minWidth: 0,
      paddingRight: 8,
      transform:
        label === 'Juzgado'
          ? 'translateX(3.5px)'
          : label === 'Identificación Unica de Expediente'
            ? 'translateX(5px)'
            : undefined,
    }}
  >
    <div style={concursalesLabelStyle}>{label}</div>
    <div style={concursalesValueStyle}>{value}</div>
  </div>
);
const ConcursalesBlock = ({ title, items }: { title: string; items: ListItem[] }) => {
  const countStart = title.lastIndexOf(' (');
  const titleLabel = countStart >= 0 ? title.slice(0, countStart) : title;
  const titleCount = countStart >= 0 ? title.slice(countStart + 1) : '';
  return (
  <Card margin={{ bottom: '4' }}>
    <div style={concursalesTitleStyle}>
      {titleLabel}
      {titleCount ? (
        <>
          {' '}
          <span style={{ marginLeft: 5 }}>{titleCount}</span>
        </>
      ) : null}
    </div>
    {items.length === 0 ? (
      <div style={emptyBand}>{emptyMsg}</div>
    ) : (
      items.map((i, idx) => (
        <div
          key={idx}
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
            width: '100%',
            alignItems: 'flex-start',
            gap: 38,
            paddingBottom: 8,
          }}
        >
          <ConcursalesField
            flex={1.1}
            label='Fecha de Presentación'
            value={String(i.fecha_presentacion || i.fechaPresentacion || i.date || i.fecha || '—')}
          />
          <ConcursalesField
            flex={1.4}
            label='Tipo de Asunto'
            value={String(
              i.tipo_descripcion ||
                i.tipo_asunto ||
                i.tipoAsunto ||
                i.type ||
                i.description ||
                i.descripcion ||
                '—'
            )}
          />
          <ConcursalesField
            flex={1.6}
            label='Identificación Unica de Expediente'
            value={String(
              i.identificacion_unica_expediente ||
                i.identificacion_expediente ||
                i.identificacionExpediente ||
                i.detail ||
                i.detalle ||
                '—'
            )}
          />
          <ConcursalesField
            flex={1.2}
            label='Juzgado'
            value={String(i.juzgado || i.institution || i.institucion || '—')}
          />
        </div>
      ))
    )}
  </Card>
  );
};

export default LidecoSemaforo;
